// Scale EAN-13 barcode parser.
//
// Layout (per the user's PDF — Σύνθετοι κωδικοί αναζήτησης, GS1):
//   digits 1-2  → prefix  (weight or price marker)
//   digits 3-7  → product code (5-digit)
//   digits 8-12 → value (price in cents, OR quantity in grams)
//   digit  13   → check digit (not validated here — the scale prints it)
//
// Two prefixes are configured in Settings › ΖΥΓΟΣ. When a scanned 13-digit
// code starts with one of them, we map it onto the product whose
// ``code`` equals digits 3-7 (zero-padded to 5).

export const SCALE_LEN = 13;

/**
 * Try to interpret a scanned string as a scale EAN-13.
 *
 * @param {string} raw        The raw scanner input
 * @param {object} settings   POS settings (must include scale_* fields)
 * @returns {null|{
 *   mode: "weight"|"price",
 *   product_code: string,
 *   value: number,
 *   raw: string,
 * }}
 */
export function parseScaleBarcode(raw, settings) {
  if (!settings?.scale_enabled) return null;
  if (typeof raw !== "string") return null;
  const s = raw.trim();
  if (s.length !== SCALE_LEN || !/^\d{13}$/.test(s)) return null;
  const prefix = s.slice(0, 2);
  const productCode = s.slice(2, 7); // already 5 chars
  const valueDigits = s.slice(7, 12);
  const value5 = parseInt(valueDigits, 10);
  if (Number.isNaN(value5)) return null;

  if (prefix === String(settings.scale_weight_prefix || "")) {
    const coef = Number(settings.scale_weight_coefficient ?? 0.001);
    return { mode: "weight", product_code: productCode, value: value5 * coef, raw: s };
  }
  if (prefix === String(settings.scale_price_prefix || "")) {
    const coef = Number(settings.scale_price_coefficient ?? 0.01);
    return { mode: "price", product_code: productCode, value: value5 * coef, raw: s };
  }
  return null;
}

/**
 * Locate a product whose ``code`` matches the 5-digit slice extracted by
 * {@link parseScaleBarcode}. Falls back to a numeric-equality check so a
 * product saved as «1» still matches «00001» from the scale.
 */
export function findProductForScaleCode(products, code5) {
  if (!products?.length || !code5) return null;
  const direct = products.find((p) => p.code === code5);
  if (direct) return direct;
  const n = parseInt(code5, 10);
  if (Number.isNaN(n)) return null;
  return products.find((p) => {
    if (!p.code) return false;
    const pn = parseInt(String(p.code), 10);
    return !Number.isNaN(pn) && pn === n;
  }) || null;
}
